import { CdkDrag, CdkDropList } from '@angular/cdk/drag-drop';
import { DatePipe, registerLocaleData } from '@angular/common';
import { HttpClientModule, provideHttpClient, withInterceptors } from '@angular/common/http';
import localeFrExtra from '@angular/common/locales/extra/fr';
import localeFr from '@angular/common/locales/fr';
import { APP_INITIALIZER, CSP_NONCE, CUSTOM_ELEMENTS_SCHEMA, DEFAULT_CURRENCY_CODE, LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DATE_LOCALE, MAT_RIPPLE_GLOBAL_OPTIONS, MatNativeDateModule, RippleGlobalOptions } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { OAuthModule, OAuthStorage } from 'angular-oauth2-oidc';
import { NgxColorsModule } from 'ngx-colors';
import { NgcCookieConsentConfig, NgcCookieConsentModule } from 'ngx-cookieconsent';
import { NgxEditorModule } from 'ngx-editor';
import { environment } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ConfigListComponent } from './components/config-list/config-list.component';
import { ConfigComponent } from './components/config/config.component';
import { LimitSettingsComponent } from './components/limit-settings/limit-settings.component';
import { LoginComponent } from './components/login/login.component';
import { NavMenuComponent } from './components/nav-menu/nav-menu.component';
import { SampleListComponent } from './components/sample-list/sample-list.component';
import { TestManagementComponent } from './components/test-management/test-management.component';
import { UserConfigurationComponent } from './components/user-configuration/user-configuration.component';
import { ErrorComponent } from './error.component';
import { CoreData } from './tools/core-data/core-data';
import { PreventDoubleClickDirective } from './tools/directive/prevent-double-click.directive';
import { LoginGuard } from './tools/login-guard';
import { secureApiInterceptor } from './tools/secure-api.interceptor';
import { AvatarComponent } from './tools/ui/avatar/avatar.component';
import { ClientSelectionBoxDialog } from './tools/ui/client-selection-box/client-selection-box';
import { ConfirmBoxDialog } from './tools/ui/confirm-box/confirm-box.component';
import { CopyConfBoxComponent } from './tools/ui/copy-conf-box/copy-conf-box.component';
import { CustomSnackBarComponent } from './tools/ui/custom-snackbar/custom-snackbar.component';
import { LimitBoxComponent } from './tools/ui/limit-box/limit-box.component';
import { LimitCategoryBoxComponent } from './tools/ui/limit-category-box/limit-category-box.component';
import { LoadingTask } from './tools/ui/loading-task';
import { ProductBoxComponent } from './tools/ui/product-box/product-box.component';
import { TestAvailableForProductBoxComponent } from './tools/ui/test-available-for-product-box/test-available-for-product-box.component';
import { TestGroupBoxComponent } from './tools/ui/test-group-box/test-group-box.component';
import { TestGroupSelectorBoxComponent } from './tools/ui/test-group-selector-box/test-group-selector-box.component';
import { UserBoxComponent } from './tools/ui/user-box/user-box.component';
import { AppConfig } from './app.config';

const cookieConfig: NgcCookieConsentConfig = {
    cookie: {
        // domain: environment.serviceBaseUrl
        domain: "https://localhost:44301/"
    },
    position: "bottom-right",
    theme: "classic",
    palette: {
        popup: {
            background: "#520000",
            text: "#ffffff",
            link: "#ffffff"
        },
        button: {
            background: "#fd680d",
            text: "#ffffff",
            border: "transparent"
        }
    },
    type: "opt-out",
    content: {
        message: "Ce site web utilise des cookies pour vous assurer la meilleure expérience de navigation sur notre site.",
        dismiss: "OK, j'ai compris!",
        deny: "Refuser",
        link: "",
        href: "",
        policy: "Cookie Policy",
        header: "Cookies sur le site!",
        allow: "Autoriser les cookies"
    }
}
const nonce = (
    document.querySelector('meta[name="CSP_NONCE"]') as HTMLMetaElement
)?.content;

export function storageFactory(): OAuthStorage {
    return localStorage;
}
export function initConfig(appConfig: AppConfig) {
    return () => appConfig.load();
}

const globalRippleConfig: RippleGlobalOptions = {
    disabled: true,
    animation: {
        enterDuration: 300,
        exitDuration: 0
    }
};
@NgModule({
    declarations: [AppComponent],
    imports: [
        BrowserModule,
        AppRoutingModule,
        HttpClientModule,
        ReactiveFormsModule,
        BrowserAnimationsModule,
        FormsModule,
        MatTableModule,
        MatSnackBarModule,
        MatFormFieldModule,
        MatExpansionModule,
        MatInputModule,
        MatButtonModule,
        MatProgressBarModule,
        MatTooltipModule,
        MatDialogModule,
        MatIconModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatPaginatorModule,
        MatAutocompleteModule,
        MatSelectModule,
        MatSortModule,
        MatChipsModule,
        MatSlideToggleModule,
        MatButtonToggleModule,
        MatStepperModule,
        MatListModule,
        MatBadgeModule,
        MatMenuModule,
        MatCheckboxModule,
        OAuthModule.forRoot(),
        NgxColorsModule,
        CdkDropList,
        CdkDrag,
        NgxEditorModule,
        NgcCookieConsentModule.forRoot(cookieConfig),
        ErrorComponent,
        AvatarComponent,
        LoginComponent,
        NavMenuComponent,
        PreventDoubleClickDirective,
        CustomSnackBarComponent,
        ConfigListComponent,
        LimitSettingsComponent,
        ConfirmBoxDialog,
        ProductBoxComponent,
        TestGroupBoxComponent,
        TestManagementComponent,
        LimitBoxComponent,
        LimitCategoryBoxComponent,
        CopyConfBoxComponent,
        ConfigComponent,
        SampleListComponent,
        UserConfigurationComponent,
        UserBoxComponent,
        ClientSelectionBoxDialog,
        TestGroupSelectorBoxComponent,
        TestAvailableForProductBoxComponent,
    ],
    providers: [
        AppConfig,
        {
            provide: APP_INITIALIZER,
            useFactory: initConfig,
            deps: [AppConfig],
            multi: true
        },
        { provide: LOCALE_ID, useValue: 'fr-FR' },
        { provide: MAT_DATE_LOCALE, useValue: 'fr-FR' },
        { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { disabled: true } },
        { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
        { provide: OAuthStorage, useFactory: storageFactory },
        { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: globalRippleConfig },
        CoreData,
        LoadingTask,
        LoginGuard,
        DatePipe,
        provideHttpClient(withInterceptors([secureApiInterceptor])),
        {
            provide: CSP_NONCE,
            useValue: nonce,
        }
    ],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule { }

registerLocaleData(localeFr, 'fr', localeFrExtra);
